import type { MenuProps } from 'antd';
import { Badge, Button, Divider, Menu, Popover, Typography } from 'antd';
import { Link } from 'react-router';

import { option, pipe, struct } from '@code-expert/prelude';
import { GuardOption } from '@code-expert/react';
import type { DistributivePick } from '@code-expert/type-utils';
import type { User } from '/imports/domain';
import { fullName, isExamUser } from '/imports/domain';
import { loginLink, logoutLink } from '/imports/startup/client/routes';
import { ActionButton } from '/imports/ui/components/ActionButton';
import { ThemeToggle } from '/imports/ui/components/ThemeSwitch';
import { Icon } from '/imports/ui/foundation/Icons';
import { styled, ThemeProvider, useTheme } from '/imports/ui/foundation/Theme';
import { messagesLink, userLink } from '/imports/ui/pages/user/routes';
import { Avatar } from './Avatar';

export interface ProfileMenuUser {
  username: string;
  firstname: string;
  lastname: string;
  canLogout: boolean;
  hasNotifications: boolean;
}

export const getProfileMenuUser = (
  user: DistributivePick<User, '_id' | 'examId' | 'username' | 'firstname' | 'lastname'>,
  hasNotifications: boolean,
): ProfileMenuUser => ({
  username: user.username,
  firstname: user.firstname,
  lastname: user.lastname,
  canLogout: !isExamUser(user),
  hasNotifications,
});

export interface ProfileMenuProps {
  user: option.Option<ProfileMenuUser>;
}

export const ProfileMenu = ({ user }: ProfileMenuProps) => {
  const { tokens } = useTheme();
  return (
    <ThemeProvider
      themeOverride={({ tokens }) => ({
        components: {
          Menu: {
            activeBarBorderWidth: 0,
            colorBgContainer: tokens.colorBgElevated,
          },
        },
      })}
    >
      <Container data-testid="profile-menu">
        <GuardOption
          value={user}
          render={({ hasNotifications }) => (
            <PolishedBadge dot={hasNotifications} offset={[-6, 6]}>
              <ActionButton label="Notifications" icon="bell" href={messagesLink({})} />
            </PolishedBadge>
          )}
        />
        <ThemeToggle />
        <Popover
          trigger="click"
          placement="bottomRight"
          content={
            // [1] Compensate for popover's hard-coded inner padding
            // [2] Compensate for popover's lack of handling overflow
            <div style={{ minWidth: 160, margin: -12 /* [1] */ }}>
              <PopoverTitle level={4}>
                <GuardOption value={user} alt={() => 'Not logged in'} render={fullName} />
              </PopoverTitle>
              <Divider style={{ margin: 0 }} />
              <Menu
                items={menuItems(user)}
                style={{
                  borderBottomLeftRadius: tokens.sizeXS /* [2] */,
                  borderBottomRightRadius: tokens.sizeXS /* [2] */,
                }}
              />
            </div>
          }
        >
          <Avatar
            user={user}
            data-username={pipe(
              user,
              option.map(struct.get('username')),
              option.getOrElse(() => 'not-logged-in'),
            )}
          />
        </Popover>
      </Container>
    </ThemeProvider>
  );
};

const menuItems: (user: option.Option<ProfileMenuUser>) => MenuProps['items'] = option.matchW(
  () => [
    {
      key: 'login',
      icon: <Icon name="circle-user" />,
      label: <Link to={loginLink}>Login</Link>,
    },
    {
      key: 'help',
      icon: <Icon name="question-circle" />,
      label: (
        <a href="https://docs.expert.ethz.ch" target="_blank" rel="noreferrer">
          Help
        </a>
      ),
    },
  ],
  (user) => [
    {
      key: 'back',
      icon: <Icon name="home" />,
      label: <Link to="/">Home</Link>,
    },
    { type: 'divider' },
    {
      key: 'profile',
      icon: <Icon name="circle-user" />,
      label: <Link to={userLink({})}>Settings</Link>,
    },
    {
      key: 'help',
      icon: <Icon name="question-circle" />,
      label: (
        <a href="https://docs.expert.ethz.ch" target="_blank" rel="noreferrer">
          Help
        </a>
      ),
    },
    ...(user.canLogout
      ? [
          { type: 'divider' as const },
          {
            key: 'logout',
            icon: <Icon name="sign-out-alt" />,
            label: (
              <form action={logoutLink({})} method="post" style={{ display: 'inline-block' }}>
                <StyledLogoutButton type="text" htmlType="submit" danger>
                  Logout
                </StyledLogoutButton>
              </form>
            ),
            danger: true,
          },
        ]
      : []),
  ],
);

// -------------------------------------------------------------------------------------------------

const Container = styled('div', ({ tokens }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: tokens.sizeSM,
  color: tokens.colorTextSecondary,
}));

const PopoverTitle = styled(Typography.Title, ({ tokens }) => ({
  paddingTop: tokens.sizeSM,
  paddingBottom: tokens.sizeXXS,
  paddingInline: tokens.size,
}));

const StyledLogoutButton = styled(Button, () => ({
  backgroundColor: 'transparent !important',
  paddingInline: 0,
  // Allow the ::after pseudo-element to escape the button's bounding box
  position: 'unset',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
  },
}));

const PolishedBadge = styled(Badge, () => ({
  '& > *': {
    display: 'grid',
    placeContent: 'center',
  },
}));
